/**
 * Plan vocabulary — the single source of truth.
 *
 * There used to be four definitions of "what plans exist", and they disagreed:
 * useSubscription had free|starter|pro|team|enterprise, agencies.ts had
 * starter|pro|agency|team|enterprise, and two separate label maps rendered the
 * SAME plan under different names — a `pro` subscriber was called "Agency" in
 * the payment-success email and "Pro" in the admin notification.
 *
 * The current commercial model (Aug 2026) is sales-led and banded on assets
 * under representation: Independent, Practice, Agency, Institution. Prices are
 * shared on the walkthrough, so nothing here carries an amount — the plan is a
 * label and an entitlement, not a price list.
 *
 * LEGACY VALUES ARE READ, NEVER REWRITTEN. Existing records still hold
 * 'starter', 'pro', 'team' and 'enterprise'. Mapping them forward at read time
 * means no migration, no downtime, and no chance of stranding a paying
 * subscriber behind a renamed tier. Do not "clean up" the database to match
 * this file; the map is the compatibility layer.
 */

/** Plans we sell today. */
export type PlanId = 'independent' | 'practice' | 'agency' | 'institution'

/** Everything a stored `plan` value can legally be, including history. */
export type StoredPlan = PlanId | 'free' | 'starter' | 'pro' | 'team' | 'enterprise'

/**
 * Legacy plan value -> the tier it corresponds to now.
 * `pro` and `team` both became Agency; `enterprise` became Institution.
 */
const LEGACY: Record<string, PlanId> = {
  starter: 'independent',
  pro: 'agency',
  team: 'agency',
  enterprise: 'institution',
}

const LABELS: Record<PlanId, string> = {
  independent: 'Independent',
  practice: 'Practice',
  agency: 'Agency',
  institution: 'Institution',
}

/**
 * Resolve any stored value to a current tier, or null when the account has no
 * paid plan. Returns null rather than guessing a default: an unprovisioned
 * account is a real state, and inventing a tier for it would show someone a
 * plan they never bought.
 */
export function resolvePlan(stored: string | null | undefined): PlanId | null {
  if (!stored) return null
  const value = String(stored).trim().toLowerCase()
  if (value === 'free' || value === '') return null
  if (value in LABELS) return value as PlanId
  return LEGACY[value] ?? null
}

/**
 * The name to show a human — in the app, in an email, anywhere. Both email
 * paths call this, which is what stops the two-names bug recurring.
 */
export function planLabel(stored: string | null | undefined): string {
  const plan = resolvePlan(stored)
  if (plan) return LABELS[plan]
  // Unrecognised but non-empty: show it verbatim rather than a made-up tier,
  // so a bad value is visible instead of silently reading as something real.
  const raw = (stored ?? '').trim()
  return raw && raw.toLowerCase() !== 'free' ? raw : 'No plan'
}

/** True when the account holds a paid tier. */
export function hasPaidPlan(stored: string | null | undefined): boolean {
  return resolvePlan(stored) !== null
}
